import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../simulator/model';
import { useSlots } from './useSlots';
import { accessFor, bookingService, bookVisit, errorDetails } from './model';

export const useWidgetBooking = ({ data, selected, pendingQuote, setPendingQuote, setResult, track, update, setErrors, go }) => {
  const prestation = bookingService(data, selected);
  const [callback, setCallback] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState(null);
  const signature = JSON.stringify({ property: data.property_type, profile: data.profile_type, surface: data.surface, adresse: data.address, selected });
  const previous = useRef(signature);
  const slots = useSlots({ prestation, adresse: data.address, enabled: Boolean(prestation) && !callback });
  const mode = Boolean(prestation) && !callback && !slots.fallback;
  useEffect(() => {
    if (signature !== previous.current) {
      previous.current = signature; setPendingQuote(null); setError(null); setConsent(false); setCallback(false);
    }
  }, [signature, setPendingQuote]);
  useEffect(() => {
    if (slots.fallback) update({ contact_preference: 'rappel' });
  }, [slots.fallback, update]);

  const verify = useCallback(async () => {
    if (!pendingQuote || !accessFor(pendingQuote)) return;
    try {
      const { data: quote } = await axios.get(`${API}/api/client/quote`, { headers: { Authorization: `Bearer ${accessFor(pendingQuote)}` } });
      if (quote.booking && quote.rdv_status === 'RDV confirmé') { setResult({ ...pendingQuote, booking: quote.booking }); setPendingQuote(null); }
      else if (quote.booking_uncertain) setError({ code: 'RESULTAT_INCONNU', uncertain: true, message: 'La demande a été transmise, mais sa confirmation reste à vérifier. Appelez-nous avant de réserver à nouveau pour éviter un doublon.' });
    } catch (err) { setError(errorDetails(err)); }
  }, [pendingQuote, setPendingQuote, setResult]);
  useEffect(() => { if (pendingQuote) verify(); }, [pendingQuote, verify]);

  const complete = async (quote) => {
    try {
      const booking = await bookVisit(quote, data, slots.slot, consent);
      setResult({ ...quote, booking }); setPendingQuote(null); setError(null);
      track('booking_confirmed', 'confirmation'); track('step_complete', 'visite');
      return true;
    } catch (err) {
      const details = errorDetails(err);
      if (details.uncertain) details.message = 'La demande a été transmise, mais sa confirmation reste à vérifier. Appelez-nous avant de réserver à nouveau pour éviter un doublon.';
      setError(details); track('booking_error', 'visite', { code: details.code });
      const aliases = { nom: 'last_name', prenom: 'first_name', telephone: 'phone', adresse: 'address' };
      setErrors(Object.fromEntries(Object.entries(details.fields || {}).map(([key, value]) => [aliases[key] || key, value])));
      if (details.code === 'CRENEAU_PRIS') slots.reload();
      if (details.code === 'CONSENTEMENT_REQUIS') setConsent(false);
      if (details.code === 'ADRESSE_INTROUVABLE') go('adresse');
      if (['HORS_ZONE', 'NON_RESERVABLE'].includes(details.code)) { setCallback(true); setError(null); update({ contact_preference: 'rappel' }); }
      return false;
    }
  };
  const preferCallback = async () => {
    update({ contact_preference: 'rappel' }); setCallback(true); setConsent(false); slots.setSlot(null);
    if (pendingQuote) {
      try {
        await axios.post(`${API}/api/client/quote/callback`, { devis_id: pendingQuote.id }, { headers: { Authorization: `Bearer ${accessFor(pendingQuote)}` } });
        setResult(pendingQuote); setPendingQuote(null); track('callback_requested', 'confirmation');
      } catch (err) { setError(errorDetails(err)); }
    }
  };
  return { slots, prestation, mode, callback, consent, setConsent, error, setError, complete, preferCallback, verify,
    returnOnline: () => { setCallback(false); setError(null); update({ contact_preference: 'email_sms' }); },
    retry: () => { setError(null); slots.reload(); } };
};